import { useMemo } from 'react';
import * as routeAddModel from '../routeAddModel';

export type JobCluster = {
  id: string;
  latitude: number;
  longitude: number;
  jobs: routeAddModel.Job[];
  isCluster: boolean;
};

type ClusteringOptions = {
  radius: number; // clustering radius in pixels
  maxZoom: number; // max zoom to cluster at
};

const defaultOptions: ClusteringOptions = {
  radius: 40,
  maxZoom: 16
};

function getDistanceInPixels(
  lat1: number, 
  lng1: number, 
  lat2: number, 
  lng2: number, 
  zoom: number
): number {
  const metersPerPixel = 156543.03392 * Math.cos(lat1 * Math.PI / 180) / Math.pow(2, zoom);
  const deltaLat = (lat2 - lat1) * 111320; // meters per degree latitude
  const deltaLng = (lng2 - lng1) * 111320 * Math.cos(lat1 * Math.PI / 180);
  const distanceInMeters = Math.sqrt(deltaLat * deltaLat + deltaLng * deltaLng);
  return distanceInMeters / metersPerPixel;
}

function clusterJobs(jobs: routeAddModel.Job[], zoom: number, options: ClusteringOptions): JobCluster[] {
  if (zoom > options.maxZoom) {
    return jobs.map(job => ({
      id: job.jobId.toString(),
      latitude: job.latitude!,
      longitude: job.longitude!,
      jobs: [job],
      isCluster: false
    }));
  }

  const clusters: JobCluster[] = [];
  const processed = new Set<number>();

  for (let i = 0; i < jobs.length; i++) {
    if (processed.has(i)) continue;
    
    const job = jobs[i];
    if (!job.latitude || !job.longitude) continue;

    const clusterJobs = [job];
    processed.add(i);

    for (let j = i + 1; j < jobs.length; j++) {
      if (processed.has(j)) continue;
      
      const otherJob = jobs[j];
      if (!otherJob.latitude || !otherJob.longitude) continue;

      const distance = getDistanceInPixels(
        job.latitude, 
        job.longitude, 
        otherJob.latitude, 
        otherJob.longitude, 
        zoom
      );

      if (distance <= options.radius) {
        clusterJobs.push(otherJob);
        processed.add(j);
      }
    }

    const centerLat = clusterJobs.reduce((sum, j) => sum + j.latitude!, 0) / clusterJobs.length;
    const centerLng = clusterJobs.reduce((sum, j) => sum + j.longitude!, 0) / clusterJobs.length;

    clusters.push({
      id: clusterJobs.length === 1 ? job.jobId.toString() : `cluster-${i}`,
      latitude: centerLat,
      longitude: centerLng,
      jobs: clusterJobs,
      isCluster: clusterJobs.length > 1
    });
  }

  return clusters;
}

export function useJobClustering(
  jobs: routeAddModel.Job[], 
  zoom: number, 
  options: Partial<ClusteringOptions> = {}
): JobCluster[] {
  const finalOptions = { ...defaultOptions, ...options };
  
  return useMemo(() => {
    return clusterJobs(jobs, zoom, finalOptions);
  }, [jobs, zoom, finalOptions.radius, finalOptions.maxZoom]);
}